export interface MeterBalanceResponse {
  balances: SmartMeterBalance[];
  unavailableBalances: UnavailableBalance[];
}

export interface SmartMeterBalance {
  // Emergency credit not deducted, so it matches the meter and in-home display.
  balanceInPence: number;
  effectiveAt: string;
  msn: string;
  mpxn: string;
  fuel: Fuel;
}

export type UnavailableBalance = {
  fuel: Fuel;
  reasons: UnavailableBalanceReason[];
  msn: string;
  mpxn: string;
};

type UnavailableBalanceReason =
  | 'TRAD_METER'
  | 'SMART_METER_BALANCE_UNAVAILABLE';

export interface MeterBalance {
  balance: number;
  date: SmartMeterBalance['effectiveAt'];
  msn: SmartMeterBalance['msn'];
  mpxn: SmartMeterBalance['mpxn'];
  fuel: SmartMeterBalance['fuel'];
}

export enum Fuel {
  GAS = 'Gas',
  ELECTRICITY = 'Electricity',
}

export type TopupInitiateRequest = {
  fuel: Fuel;
  topUpPoundsInt: number;
  mpxn: string;
  msn: string;
  channel: 'ovo_account_web';
};

export type TopupInitiateResponse = {
  topupSessionId: string;
  paymentSessionUrl: string;
};

export enum TransactionState {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

type TopupHistoryItemBase = {
  msn: string;
  amountInPence: number;
  transactionDateTime: string;
  topupType?: 'asc';
};

export type TopupHistoryItem =
  | (TopupHistoryItemBase & {
      state: TransactionState.PENDING;
      detailedState: 'PROCESSING';
    })
  | (TopupHistoryItemBase & {
      utrn: string;
      state: TransactionState.SUCCESS;
      detailedState: 'SUCCESS';
    })
  | (TopupHistoryItemBase & {
      utrn: string;
      state: TransactionState.SUCCESS;
      detailedState: 'APPLY_FAILED_NO_ACTION';
    })
  | (TopupHistoryItemBase & {
      utrn: string;
      state: TransactionState.SUCCESS;
      detailedState: 'APPLY_FAILED_CONTACT_US';
      error: string;
    })
  | (TopupHistoryItemBase & {
      state: TransactionState.FAILURE;
      detailedState: 'VEND_FAILED_NO_ACTION';
    })
  | (TopupHistoryItemBase & {
      state: TransactionState.FAILURE;
      detailedState: 'VEND_FAILED_CONTACT_US';
      error: string;
    });

export type BannerAction = 'emergency_credit_help';

export type BannerType = 'error' | 'info' | 'success' | 'warning';

export type BannerConfig = {
  type: BannerType;
  heading: string;
  shortMessage: string;
  longMessage: string;
  icon?: string;
  moreActions: BannerAction[];
};

export interface TopupConfig {
  minTopupAmountInPence: number;
  maxTopupAmountInPence: number;
  maxBalanceInPence: number;
  banner?: BannerConfig;
  topupsBlocked: boolean;
}

export type TopupResponse<K extends string, T extends unknown[]> = {
  [P in K]: T;
};

/**
 * Base properties shared by all pending topup states
 */
type BasePendingTopup = {
  fuel: Fuel;
  topupSessionId: string;
  msn: string;
  topupType?: 'asc';
};

/**
 * Type representing a pending topup with discriminated union based on state
 */
export type PendingTopup =
  | (BasePendingTopup & { state: 'PENDING_PAYMENT' })
  | (BasePendingTopup & {
      state: 'PROCESSING';
      expectedMinsToAutoApply: number;
    })
  | (BasePendingTopup & {
      state: 'AUTO_APPLY_DELAYED';
      utrn: string;
      expectedMinsToAutoApply: number;
    })
  | (BasePendingTopup & {
      state: 'SUCCESSFULLY_APPLIED';
    })
  | (BasePendingTopup & {
      state: 'MANUALLY_ENTER_UTRN';
      utrn: string;
    })
  | (BasePendingTopup & { state: 'TRY_AGAIN_LATER' })
  | (BasePendingTopup & {
      state: 'CONTACT_US';
      errors: Array<{ code: string }> | null;
    })
  | (BasePendingTopup & {
      state: 'CONTACT_US_APPLY_FAILED';
      errors: Array<{ code: string }> | null;
    })
  | {
      fuel?: Fuel;
      topupSessionId?: string;
      msn?: string;
      state: 'INVALID_STATE';
      utrn?: string | null;
    };

export type VALID_TOPUP_STATES = Exclude<
  Array<PendingTopup['state']>[number],
  'INVALID_STATE'
>;

export type TopupWithState<
  U extends Record<K, string>,
  S extends U[K],
  K extends string = 'state',
> = U extends Record<K, S> ? U : never;

/**
 * Filters an array of topups to only include those with specific states.
 * @example filterTopupsByStates(topups, 'PROCESSING')
 * @example filterTopupsByStates(topups, ['PROCESSING', 'PENDING_PAYMENT'])
 */
export const filterTopupsByStates = <S extends PendingTopup['state']>(
  topups: PendingTopup[],
  stateOrStates: S | ReadonlyArray<S>,
): TopupWithState<PendingTopup, S>[] => {
  const states = Array.isArray(stateOrStates) ? stateOrStates : [stateOrStates];
  return topups.filter((topup): topup is TopupWithState<PendingTopup, S> =>
    (states as ReadonlyArray<PendingTopup['state']>).includes(topup.state),
  );
};
